import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router';
import Konva from 'konva';
import { FieldCanvas, FieldTransform, FieldOrientation } from '../components/FieldCanvas';
import { GridLayer } from '../components/GridLayer';
import { BunkerLayer } from '../components/BunkerLayer';
import { BunkerPalette } from '../components/BunkerPalette';
import { BUNKER_CATALOG, TilesetKey, getBunkersForTileset } from '../lib/bunkerCatalog';
import { useLayoutState, generateId } from '../lib/layoutState';
import { mirrorRotation } from '../lib/fieldCoords';
import { buildArenaSpec, downloadArenaJson, importArenaJson, sanitizeName, validateArenaSpec } from '../lib/arenaExport';
import { ReferenceImageLayer } from '../components/ReferenceImageLayer';
import { StarterBrickLayer } from '../components/StarterBrickLayer';
import { usePlayfabAuthContext } from '../contexts/PlayfabAuthContext';

// sessionStorage key for autosaving in-progress work so it survives
// navigating away from /field-editor and back (e.g. via the Login link).
const DRAFT_STORAGE_KEY = 'fieldEditorDraft';

type EditMode = { itemId: string; friendlyId?: string; name: string };

// Passed via history.push({state}) from MyLayouts.tsx's Edit button.
type FieldEditorLocationState = {
  itemId?: string;
  friendlyId?: string;
  name?: string;
  contentUrl?: string;
};

export const FieldEditor: React.FC = () => {
  const canvasContainerRef = useRef<HTMLDivElement>(null);
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });
  const [stage, setStage] = useState<Konva.Stage | null>(null);
  const [fieldTransform, setFieldTransform] = useState<FieldTransform | null>(null);

  const [orientation, setOrientation] = useState<FieldOrientation>('horizontal');
  const [gridVisible, setGridVisible] = useState(true);
  const [snapFt, setSnapFt] = useState(1);

  const [tileset, setTileset] = useState<TilesetKey>('AIR_NXL');
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [mirrorEnabled, setMirrorEnabled] = useState(false);
  const [rotSnapDeg, setRotSnapDeg] = useState(15);
  const [layoutName, setLayoutName] = useState('');
  const [uploadBusy, setUploadBusy] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [editMode, setEditMode] = useState<EditMode | null>(null);

  const playfab = usePlayfabAuthContext();
  const history = useHistory();
  const location = useLocation<FieldEditorLocationState | undefined>();

  const editItemId = useMemo(
    () => new URLSearchParams(location.search).get('editItemId'),
    [location.search]
  );

  const [refImageSrc, setRefImageSrc] = useState<string | null>(null);
  const [refOpacity, setRefOpacity] = useState(0.3);
  const [refScaleX, setRefScaleX] = useState(1);
  const [refScaleY, setRefScaleY] = useState(1);
  const [refScaleSplit, setRefScaleSplit] = useState(false);
  const [refImageLocked, setRefImageLocked] = useState(false);
  const refImageFileRef = useRef<HTMLInputElement>(null);

  const handleRefImageFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    const reader = new FileReader();
    reader.onload = (ev) => {
      setRefImageSrc(ev.target?.result as string);
      setRefScaleX(1); setRefScaleY(1); setRefScaleSplit(false); setRefImageLocked(false);
    };
    reader.readAsDataURL(file);
  };

  const {
    placedBunkers, setLayout, replaceLayout,
    addBunker, removeBunker, removeBunkers, updateBunker,
    undo, redo, canUndo, canRedo,
  } = useLayoutState();

  // Restore an in-progress layout on mount (e.g. after the "Login" link
  // navigates away from /field-editor and back, which unmounts this
  // component and would otherwise silently discard unsaved work). Skipped
  // when editItemId is in the URL — the dedicated edit-load effect below
  // always loads fresh from the server in that case.
  const draftRestored = useRef(false);
  useEffect(() => {
    if (draftRestored.current) return;
    draftRestored.current = true;
    if (editItemId) return;
    try {
      const raw = sessionStorage.getItem(DRAFT_STORAGE_KEY);
      if (!raw) return;
      const draft = JSON.parse(raw);
      if (Array.isArray(draft.placedBunkers) && draft.placedBunkers.length > 0) {
        replaceLayout(draft.placedBunkers);
      }
      if (draft.tileset) setTileset(draft.tileset);
      if (draft.orientation) setOrientation(draft.orientation);
      if (typeof draft.layoutName === 'string') setLayoutName(draft.layoutName);
      // Restores edit mode without re-fetching if the user navigated away
      // mid-edit (e.g. via the Login link, which drops the ?editItemId=
      // query string) and back — the draft already has their edits.
      if (typeof draft.editItemId === 'string') {
        setEditMode({
          itemId: draft.editItemId,
          friendlyId: draft.editFriendlyId,
          name: draft.editName || draft.layoutName || '',
        });
      }
    } catch (e) {
      console.warn('Failed to restore field editor draft:', e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editItemId]);

  // Autosave the in-progress layout so it survives navigating away and back.
  useEffect(() => {
    try {
      sessionStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify({
        placedBunkers, tileset, orientation, layoutName,
        editItemId: editMode?.itemId,
        editFriendlyId: editMode?.friendlyId,
        editName: editMode?.name,
      }));
    } catch {
      // sessionStorage unavailable/full — non-fatal, just skip autosave.
    }
  }, [placedBunkers, tileset, orientation, layoutName, editMode]);

  // Loads an existing layout for editing when ?editItemId= is present in
  // the URL. Always loads fresh from the server — never reuses the
  // sessionStorage draft (handled above) for this path.
  const editLoadStarted = useRef<string | null>(null);
  useEffect(() => {
    if (!editItemId || editLoadStarted.current === editItemId) return;
    editLoadStarted.current = editItemId;

    const load = async () => {
      const state = location.state;
      let text: string | null = null;
      let name = state?.name;
      let friendlyId = state?.friendlyId;

      if (state?.contentUrl) {
        try {
          const res = await fetch(state.contentUrl);
          if (res.ok) text = await res.text();
        } catch {
          // CORS or network failure — fall through to the server-side fallback.
        }
      }

      if (!text) {
        const result = await playfab.api.getArenaLayoutForEdit(editItemId);
        if (!result.success || !result.arenaSpec) {
          alert(`Could not load this layout for editing.\n\n${result.error || ''}`);
          history.replace('/my-layouts');
          return;
        }
        text = JSON.stringify(result.arenaSpec);
        name = result.name;
        friendlyId = result.friendlyId;
      }

      const { bunkers, tileset: importedTileset, error } = importArenaJson(text);
      if (error) {
        alert(`Could not load this layout for editing.\n\n${error}`);
        history.replace('/my-layouts');
        return;
      }

      replaceLayout(bunkers);
      setTileset(importedTileset);
      setLayoutName(name || '');
      setEditMode({ itemId: editItemId, friendlyId, name: name || '' });
    };

    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editItemId]);

  // A successful upload disables the button (see the disabled= condition
  // below) until the layout or name actually changes again — guards against
  // reflexive double-clicks without blocking a deliberate re-upload.
  useEffect(() => {
    setUploadSuccess(false);
  }, [placedBunkers, layoutName]);

  const [spriteImages, setSpriteImages] = useState<Record<string, HTMLImageElement>>({});

  // Pre-load all sprite images for the active tileset so BunkerLayer can render synchronously.
  useEffect(() => {
    setSpriteImages({});
    for (const def of getBunkersForTileset(tileset)) {
      const sprite = def.sprites?.[tileset];
      if (!sprite) continue;
      const img = new Image();
      img.onload = () => setSpriteImages(prev => ({ ...prev, [def.type]: img }));
      img.src = sprite.spriteUrl;
    }
  }, [tileset]);

  const applySymmetry = () => {
    const toAdd: import('../lib/layoutState').PlacedBunkerState[] = [];
    for (const b of placedBunkers) {
      if (Math.abs(b.zFt) < 1) continue; // on/near centerline — skip
      const mirrorZ = -b.zFt;
      const alreadyMirrored = placedBunkers.some(
        other => other.id !== b.id
          && Math.abs(other.xFt - b.xFt) < 2
          && Math.abs(other.zFt - mirrorZ) < 2,
      );
      if (!alreadyMirrored) {
        // Wing bunkers keep their rotation so the taper stays on the same absolute side.
        const mirRot = bunkerDefs[b.type]?.taperEdge
          ? b.rotationDeg
          : mirrorRotation(b.rotationDeg, orientation);
        toAdd.push({
          id: generateId(),
          type: b.type,
          xFt: b.xFt,
          zFt: mirrorZ,
          rotationDeg: mirRot,
        });
      }
    }
    if (toAdd.length > 0) setLayout([...placedBunkers, ...toAdd]);
  };

  const clearField = () => {
    if (placedBunkers.length === 0) return;
    if (window.confirm('Clear all bunkers from the field?')) {
      setLayout([]);
    }
  };

  const importFileRef = useRef<HTMLInputElement>(null);

  const handleImportFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Reset so the same file can be re-imported later
    e.target.value = '';
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target?.result as string;
      const { bunkers, tileset: importedTileset, skipped, error } = importArenaJson(text);
      if (error) {
        alert(`Import failed: ${error}`);
        return;
      }
      setLayout(bunkers);
      setTileset(importedTileset);
      const importedName = sanitizeName(file.name.replace(/\.arena\.json$/i, '').replace(/\.json$/i, ''));
      if (importedName) setLayoutName(importedName);
      if (skipped > 0) {
        // Non-blocking notice — don't use alert for this
        console.warn(`Import: ${skipped} object(s) skipped (unknown or auto-placed types)`);
      }
    };
    reader.readAsText(file);
  };

  const handleUpload = async () => {
    const name = layoutName.trim();
    if (!editMode && !name) {
      alert('Enter a layout name before uploading.');
      return;
    }

    const spec = buildArenaSpec(placedBunkers, tileset, orientation);
    const validationError = validateArenaSpec(spec);
    if (validationError) {
      alert(`Upload failed: ${validationError}`);
      return;
    }

    setUploadBusy(true);
    setUploadError(null);
    setUploadSuccess(false);
    const result = editMode
      ? await playfab.api.updateArenaLayout(editMode.itemId, spec)
      : await playfab.api.uploadArenaLayout(name, spec);
    setUploadBusy(false);
    if (result.success) {
      setUploadSuccess(true);
    } else {
      setUploadError(result.error || (editMode ? 'Update failed.' : 'Upload failed.'));
    }
  };

  const handleExitEditMode = () => {
    if (placedBunkers.length > 0 && !window.confirm('Discard the current layout and start a new one?')) {
      return;
    }
    setEditMode(null);
    setLayout([]);
    setLayoutName('');
    setUploadSuccess(false);
    setUploadError(null);
    history.replace('/field-editor');
  };

  // When the user flips orientation the canvas axes swap, so bunker rotations must
  // be adjusted by the same 90° transform to keep bunkers facing the same direction.
  // Uses replaceLayout so the transform doesn't pollute the undo history.
  const prevOrientationRef = useRef(orientation);
  useEffect(() => {
    if (prevOrientationRef.current === orientation) return;
    prevOrientationRef.current = orientation;
    if (placedBunkers.length === 0) return;
    replaceLayout(placedBunkers.map(b => ({
      ...b,
      rotationDeg: (270 - b.rotationDeg + 360) % 360,
    })));
  // placedBunkers intentionally omitted — we only want this to fire on orientation change
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orientation]);

  // Ctrl+Z / Ctrl+Y (also Ctrl+Shift+Z for redo on Mac convention)
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      const tag = (document.activeElement as HTMLElement)?.tagName ?? '';
      if (tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA') return;
      if (e.code === 'KeyZ' && !e.shiftKey) { undo(); e.preventDefault(); }
      if (e.code === 'KeyY' || (e.code === 'KeyZ' && e.shiftKey)) { redo(); e.preventDefault(); }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [undo, redo]);

  const bunkerDefs = useMemo(() => {
    const map: Record<string, import('../lib/bunkerCatalog').BunkerDef> = {};
    for (const def of BUNKER_CATALOG) map[def.type] = def;
    return map;
  }, []);

  useEffect(() => {
    const measure = () => {
      if (canvasContainerRef.current) {
        setCanvasSize({
          width:  canvasContainerRef.current.clientWidth,
          height: canvasContainerRef.current.clientHeight,
        });
      }
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  const handleCanvasReady = useCallback((s: Konva.Stage, t: FieldTransform) => {
    setStage(s);
    setFieldTransform(t);
  }, []);

  const fitToScreen = () => {
    const fn = (window as any).__fieldFitToScreen;
    if (fn) fn();
  };

  const toggleOrientation = () =>
    setOrientation(o => o === 'horizontal' ? 'vertical' : 'horizontal');

  return (
    <div className="flex flex-col" style={{ height: 'calc(100vh - 56px)' }}>
      {/* Toolbar */}
      <div className="flex flex-row items-center gap-4 px-3 py-1 bg-[rgba(0,0,0,0.7)] text-xs flex-shrink-0 flex-wrap">
        <button
          onClick={fitToScreen}
          className="px-2 py-1 rounded bg-[#0095da] hover:opacity-80"
        >
          Fit
        </button>

        <button
          onClick={toggleOrientation}
          className="px-2 py-1 rounded bg-[#444] hover:opacity-80"
        >
          {orientation === 'horizontal' ? '↔ Horizontal' : '↕ Vertical'}
        </button>

        <label className="flex items-center gap-1 cursor-pointer">
          <input
            type="checkbox"
            checked={gridVisible}
            onChange={e => setGridVisible(e.target.checked)}
            className="accent-[#0095da]"
          />
          Grid
        </label>

        <label className="flex items-center gap-1">
          Move
          <select
            value={snapFt}
            onChange={e => setSnapFt(Number(e.target.value))}
            className="bg-[#333] rounded px-1 py-0.5"
          >
            <option value={0}>Free</option>
            {[1, 2, 5, 10].map(n => (
              <option key={n} value={n}>{n} ft</option>
            ))}
          </select>
        </label>

        <label className="flex items-center gap-1">
          Rotate
          <select
            value={rotSnapDeg}
            onChange={e => setRotSnapDeg(Number(e.target.value))}
            className="bg-[#333] rounded px-1 py-0.5"
          >
            <option value={0}>Free</option>
            {[15, 30, 45, 90].map(n => (
              <option key={n} value={n}>{n}°</option>
            ))}
          </select>
        </label>

        <label className="flex items-center gap-1 cursor-pointer">
          <input
            type="checkbox"
            checked={mirrorEnabled}
            onChange={e => setMirrorEnabled(e.target.checked)}
            className="accent-[#0095da]"
          />
          Mirror
        </label>

        <button
          onClick={applySymmetry}
          className="px-2 py-1 rounded bg-[#444] hover:opacity-80"
        >
          Apply Symmetry
        </button>

        {/* Divider */}
        <div className="w-px h-4 bg-white/20 flex-shrink-0" />

        <button
          onClick={undo}
          disabled={!canUndo}
          title="Undo (Ctrl+Z)"
          className="px-2 py-1 rounded bg-[#444] hover:opacity-80 disabled:opacity-30 disabled:cursor-not-allowed"
        >
          Undo
        </button>

        <button
          onClick={redo}
          disabled={!canRedo}
          title="Redo (Ctrl+Y)"
          className="px-2 py-1 rounded bg-[#444] hover:opacity-80 disabled:opacity-30 disabled:cursor-not-allowed"
        >
          Redo
        </button>

        <button
          onClick={clearField}
          title="Clear all bunkers"
          className="px-2 py-1 rounded bg-[#5a2020] hover:opacity-80"
        >
          Clear
        </button>

        {/* Divider */}
        <div className="w-px h-4 bg-white/20 flex-shrink-0" />

        <input
          type="text"
          placeholder="Layout name…"
          value={layoutName}
          disabled={!!editMode}
          title={editMode ? "Renaming isn't supported — use New Layout, then delete and re-upload under a new name to rename." : undefined}
          onChange={e => {
            setLayoutName(sanitizeName(e.target.value));
            setUploadError(null);
            setUploadSuccess(false);
          }}
          className={`bg-[#333] rounded px-2 py-0.5 text-white placeholder-gray-500 outline-none w-32 ${editMode ? 'opacity-50 cursor-not-allowed' : ''}`}
        />

        <button
          onClick={() => {
            const name = layoutName.trim() || 'layout';
            downloadArenaJson(placedBunkers, tileset, orientation, name);
          }}
          disabled={placedBunkers.length === 0}
          title="Export arena JSON for Snapshot VR"
          className="px-2 py-1 rounded bg-[#1a6b35] hover:opacity-80 disabled:opacity-30 disabled:cursor-not-allowed"
        >
          Export
        </button>

        <input
          ref={importFileRef}
          type="file"
          accept=".json"
          onChange={handleImportFile}
          style={{ display: 'none' }}
        />
        <button
          onClick={() => importFileRef.current?.click()}
          title="Import a previously exported .arena.json"
          className="px-2 py-1 rounded bg-[#444] hover:opacity-80"
        >
          Import
        </button>

        {playfab.isLoggedIn && (
          <button
            onClick={handleUpload}
            disabled={placedBunkers.length === 0 || (!editMode && layoutName.trim().length === 0) || uploadBusy || uploadSuccess}
            title={editMode ? 'Upload changes to this layout' : 'Upload this layout to the game'}
            className="px-2 py-1 rounded bg-[#2050a0] hover:opacity-80 disabled:opacity-30 disabled:cursor-not-allowed"
          >
            {uploadBusy ? 'Uploading…' : (editMode ? 'Upload Changes' : 'Upload to Game')}
          </button>
        )}
        {editMode && (
          <button
            onClick={handleExitEditMode}
            title="Stop editing this layout and start a new one"
            className="px-2 py-1 rounded bg-[#444] hover:opacity-80"
          >
            New Layout
          </button>
        )}
        {uploadSuccess && <span className="text-[#4caf50]">Uploaded!</span>}
        {uploadError && <span className="text-[#C00]">{uploadError}</span>}

        {/* Divider */}
        <div className="w-px h-4 bg-white/20 flex-shrink-0" />

        <input
          ref={refImageFileRef}
          type="file"
          accept="image/*"
          onChange={handleRefImageFile}
          style={{ display: 'none' }}
        />
        {refImageSrc ? (
          <>
            {refScaleSplit ? (
              <>
                <label className="flex items-center gap-1">
                  Scale X
                  <input
                    type="range"
                    min={10} max={400} step={1}
                    value={Math.round(refScaleX * 100)}
                    onChange={e => setRefScaleX(Number(e.target.value) / 100)}
                    className="w-20 accent-[#0095da]"
                    title="Horizontal scale (rough)"
                  />
                  <input
                    type="number"
                    min={10} max={400} step={0.1}
                    value={Math.round(refScaleX * 1000) / 10}
                    onChange={e => {
                      const v = parseFloat(e.target.value);
                      if (!isNaN(v) && v >= 10 && v <= 400) setRefScaleX(v / 100);
                    }}
                    className="bg-[#333] rounded px-1 py-0.5 w-16 text-right"
                    title="Horizontal scale % — arrow keys for ±0.1% steps"
                  />
                  <span className="opacity-60">%</span>
                </label>
                <label className="flex items-center gap-1">
                  Scale Y
                  <input
                    type="range"
                    min={10} max={400} step={1}
                    value={Math.round(refScaleY * 100)}
                    onChange={e => setRefScaleY(Number(e.target.value) / 100)}
                    className="w-20 accent-[#0095da]"
                    title="Vertical scale (rough)"
                  />
                  <input
                    type="number"
                    min={10} max={400} step={0.1}
                    value={Math.round(refScaleY * 1000) / 10}
                    onChange={e => {
                      const v = parseFloat(e.target.value);
                      if (!isNaN(v) && v >= 10 && v <= 400) setRefScaleY(v / 100);
                    }}
                    className="bg-[#333] rounded px-1 py-0.5 w-16 text-right"
                    title="Vertical scale % — arrow keys for ±0.1% steps"
                  />
                  <span className="opacity-60">%</span>
                </label>
              </>
            ) : (
              <label className="flex items-center gap-1">
                Scale
                <input
                  type="range"
                  min={10} max={400} step={1}
                  value={Math.round(refScaleX * 100)}
                  onChange={e => { const v = Number(e.target.value) / 100; setRefScaleX(v); setRefScaleY(v); }}
                  className="w-20 accent-[#0095da]"
                  title="Reference image scale (rough)"
                />
                <input
                  type="number"
                  min={10} max={400} step={0.1}
                  value={Math.round(refScaleX * 1000) / 10}
                  onChange={e => {
                    const v = parseFloat(e.target.value);
                    if (!isNaN(v) && v >= 10 && v <= 400) { setRefScaleX(v / 100); setRefScaleY(v / 100); }
                  }}
                  className="bg-[#333] rounded px-1 py-0.5 w-16 text-right"
                  title="Scale % — arrow keys for ±0.1% steps"
                />
                <span className="opacity-60">%</span>
              </label>
            )}
            <label className="flex items-center gap-1 cursor-pointer">
              <input
                type="checkbox"
                checked={refScaleSplit}
                onChange={e => {
                  const split = e.target.checked;
                  setRefScaleSplit(split);
                  if (!split) setRefScaleY(refScaleX);
                }}
                className="accent-[#0095da]"
              />
              Split
            </label>
            <label className="flex items-center gap-1">
              Opacity
              <input
                type="range"
                min={0} max={1} step={0.05}
                value={refOpacity}
                onChange={e => setRefOpacity(Number(e.target.value))}
                className="w-20 accent-[#0095da]"
                title="Reference image opacity"
              />
            </label>
            <label className="flex items-center gap-1 cursor-pointer" title="Lock image in place to enable drag-select over it">
              <input
                type="checkbox"
                checked={refImageLocked}
                onChange={e => setRefImageLocked(e.target.checked)}
                className="accent-[#0095da]"
              />
              Lock
            </label>
            <button
              onClick={() => { setRefImageSrc(null); setRefScaleX(1); setRefScaleY(1); setRefScaleSplit(false); setRefImageLocked(false); }}
              title="Remove reference image"
              className="px-2 py-1 rounded bg-[#5a2020] hover:opacity-80"
            >
              ✕ Ref
            </button>
          </>
        ) : (
          <button
            onClick={() => refImageFileRef.current?.click()}
            title="Import a reference image to trace over"
            className="px-2 py-1 rounded bg-[#444] hover:opacity-80"
          >
            Ref Image
          </button>
        )}

        <span className="opacity-40">Scroll to zoom · Space+drag to pan · R rotates · ↑↓←→ move 1ft · Del removes · Ctrl+Z/Y undo/redo</span>
      </div>

      {/* Main layout */}
      <div className="flex flex-row flex-1 overflow-hidden">
        {/* Palette sidebar */}
        <div className="w-48 flex-shrink-0 bg-[rgba(0,0,0,0.55)] overflow-hidden flex flex-col">
          <BunkerPalette
            tileset={tileset}
            onTilesetChange={setTileset}
            placedBunkers={placedBunkers}
            selectedType={selectedType}
            onSelectType={setSelectedType}
          />
        </div>

        {/* Canvas area */}
        <div ref={canvasContainerRef} className="flex-1 bg-[#1a1a1a]">
          {canvasSize.width > 0 && (
            <FieldCanvas
              containerWidth={canvasSize.width}
              containerHeight={canvasSize.height}
              orientation={orientation}
              onReady={handleCanvasReady}
            />
          )}
          {stage && fieldTransform && (
            <StarterBrickLayer
              stage={stage}
              transform={fieldTransform}
              tileset={tileset}
              orientation={orientation}
            />
          )}
          {stage && fieldTransform && (
            <GridLayer
              stage={stage}
              transform={fieldTransform}
              visible={gridVisible}
              snapFt={snapFt}
            />
          )}
          {stage && fieldTransform && (
            <BunkerLayer
              stage={stage}
              transform={fieldTransform}
              placedBunkers={placedBunkers}
              bunkerDefs={bunkerDefs}
              tileset={tileset}
              spriteImages={spriteImages}
              orientation={orientation}
              selectedType={selectedType}
              snapFt={snapFt}
              rotSnapDeg={rotSnapDeg}
              mirrorEnabled={mirrorEnabled}
              onPlace={addBunker}
              onUpdate={updateBunker}
              onRemove={removeBunker}
              onRemoveMany={removeBunkers}
              onExitPlacement={() => setSelectedType(null)}
            />
          )}
          {stage && fieldTransform && refImageSrc && (
            <ReferenceImageLayer
              stage={stage}
              transform={fieldTransform}
              imageSrc={refImageSrc}
              opacity={refOpacity}
              scaleX={refScaleX}
              scaleY={refScaleY}
              locked={refImageLocked || selectedType !== null}
            />
          )}
        </div>
      </div>
    </div>
  );
};
